import {
  cloneElement,
  createContext,
  isValidElement,
  useContext,
  type ReactElement,
  type ReactNode,
} from 'react';

// Threaded "parent path" so nested <Commentable>s auto-derive their full path.
// At the root, parent is the empty string — child becomes "AIRail". One level
// deeper, the same wrapper sees parent="AIRail" and computes "AIRail.Input".
const CommentPathContext = createContext<string>('');

type CommentableProps = {
  /** Local name; the wrapper prepends any parent path with `.` */
  name: string;
  /** Single React element to stamp with data-comment-anchor. */
  children: ReactNode;
};

/**
 * Mark a region as commentable for the in-app overlay.
 *
 * Renders nothing of its own — uses React.cloneElement to add a
 * `data-comment-anchor` attribute to its single child element. Children that
 * are themselves <Commentable> read the path via context and stack their
 * local name onto it, so:
 *
 *   <Commentable name="AIRail">
 *     <aside className="relative flex h-full w-full flex-col">
 *       <Commentable name="Composer">
 *         <div className="sticky bottom-0">…</div>
 *       </Commentable>
 *     </aside>
 *   </Commentable>
 *
 * produces an <aside data-comment-anchor="AIRail"> with a nested
 * <div data-comment-anchor="AIRail.Composer"> — zero new DOM nodes, zero
 * layout impact, and the path is derived automatically.
 *
 * The wrapper lives under src/shared/ (not src/versions/v<N>/) so a single
 * import survives the v1 → v2 fork. Each frozen version's registry decides
 * what names are valid; this component just sets the attribute.
 */
export function Commentable({ name, children }: CommentableProps) {
  const parent = useContext(CommentPathContext);
  const fullPath = parent ? `${parent}.${name}` : name;

  // Require a single React element so cloneElement can attach the attribute
  // without introducing a wrapper div. If callers pass a fragment or string,
  // the type system will warn; at runtime we fall back to a span so we never
  // silently lose the anchor.
  const target = isValidElement(children) ? (children as ReactElement) : <span>{children}</span>;

  const stamped = cloneElement(target, {
    'data-comment-anchor': fullPath,
  } as Record<string, string>);

  return (
    <CommentPathContext.Provider value={fullPath}>
      {stamped}
    </CommentPathContext.Provider>
  );
}
